import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { getEntity, getProjectWarRoom } from "@/lib/api";
import { entityLabel, fmtUSD } from "@/lib/format";

export default function ProjectWarRoomPage() {
  const { slug } = useParams();
  const [entity, setEntity] = useState(null);
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const e = await getEntity(slug);
        if (!alive) return;
        setEntity(e);
        const wr = await getProjectWarRoom(e.id);
        if (!alive) return;
        setRoom(wr);
        setError(null);
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.status === 403
          ? "Only the claimed project operator can open War Room."
          : (err?.response?.data?.detail || "War Room unavailable"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const clrSettled = room?.community_leverage_ratio_settled;
  const clrAllocated = room?.community_leverage_ratio_allocated;
  const clr = clrSettled ?? room?.community_leverage_ratio?.community_leverage_ratio ?? clrAllocated;

  return (
    <div className="min-h-screen grain">
      <Navbar category={entity?.category === "callers" ? "callers" : "tokens"} />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <Link to={entity ? `/${entity.category === "callers" ? "caller" : "token"}/${entity.slug}` : "/"} className="text-xs text-zinc-500 hover:text-white">
          ← Profile
        </Link>
        <h1 className="mt-4 font-display text-3xl font-black tracking-tight">
          {entity ? `${entityLabel(entity)} War Room` : "War Room"}
        </h1>
        <p className="mt-2 text-sm text-zinc-400">
          Project competition + sponsored incentive overview. Verified Backing alone determines rank.
        </p>

        {loading && <div className="mt-8 text-sm text-zinc-500">Loading…</div>}
        {error && <div className="mt-8 text-sm text-zinc-400" data-testid="war-room-error">{error}</div>}

        {room && (
          <div className="mt-8 space-y-6" data-testid="war-room">
            <section className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-white/10 px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Verified Backing</div>
                <div className="mt-1 font-mono text-xl font-bold text-gold">{fmtUSD(room.total_verified_backing_usd)}</div>
              </div>
              <div className="rounded-xl border border-white/10 px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">All-time rank</div>
                <div className="mt-1 font-mono text-xl font-bold">#{room.all_time_rank ?? "—"}</div>
              </div>
              <div className="rounded-xl border border-white/10 px-4 py-3" data-testid="war-room-clr">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">
                  {clrSettled != null ? "CLR settled" : "Community Leverage"}
                </div>
                <div className="mt-1 font-mono text-xl font-bold">{clr != null ? `${clr}x` : "—"}</div>
              </div>
            </section>

            <section className="grid gap-3 sm:grid-cols-3" data-testid="war-room-incentives">
              <div className="rounded-xl border border-white/10 px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Funded (base)</div>
                <div className="mt-1 font-mono text-sm">{room.incentives_committed_base_units ?? 0}</div>
              </div>
              <div className="rounded-xl border border-white/10 px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Allocated / Settled</div>
                <div className="mt-1 font-mono text-sm">
                  {room.incentives_allocated_base_units ?? 0} / {room.incentives_settled_base_units ?? 0}
                </div>
              </div>
              <div className="rounded-xl border border-white/10 px-4 py-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600">Claim rate</div>
                <div className="mt-1 font-mono text-sm">
                  {room.claim_rate != null ? `${(room.claim_rate * 100).toFixed(1)}%` : "—"}
                </div>
              </div>
            </section>

            <section>
              <h2 className="mb-2 text-[10px] uppercase tracking-[0.2em] text-zinc-600">Battles</h2>
              <div className="space-y-2">
                {(room.battles || []).map((b) => (
                  <Link
                    key={b.id}
                    to={`/battle/${b.slug}`}
                    className="block rounded-xl border border-white/10 px-4 py-3 text-sm hover:border-white/25"
                  >
                    <div className="font-display font-bold">{b.title || b.slug}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wider text-zinc-500">
                      {b.status}{b.result_status ? ` · ${b.result_status}` : ""}
                      {b.reward_pool_id ? " · sponsored" : ""}
                    </div>
                  </Link>
                ))}
                {!(room.battles || []).length && (
                  <div className="rounded-xl border border-white/10 px-4 py-6 text-sm text-zinc-500">No Battles yet.</div>
                )}
              </div>
            </section>

            <section>
              <h2 className="mb-2 text-[10px] uppercase tracking-[0.2em] text-zinc-600">Reward pools</h2>
              <div className="space-y-2" data-testid="war-room-pools">
                {(room.reward_pools || []).map((p) => (
                  <div key={p.id} className="rounded-xl border border-white/10 px-4 py-3 text-sm">
                    <div className="font-display font-bold">{p.slug}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wider text-zinc-500">
                      {p.status}
                      {p.conversion_mode ? ` · ${p.conversion_mode}` : ""}
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-zinc-600">
                      funded {p.funded_amount_base_units ?? 0}
                      {" · "}alloc {p.allocated_amount_base_units ?? 0}
                      {" · "}settled {p.settled_amount_base_units ?? 0}
                    </div>
                  </div>
                ))}
                {!(room.reward_pools || []).length && (
                  <div className="rounded-xl border border-white/10 px-4 py-6 text-sm text-zinc-500">No reward pools.</div>
                )}
              </div>
            </section>

            <p className="text-xs text-zinc-600">{room.disclaimer}</p>
          </div>
        )}
      </main>
    </div>
  );
}
